import {
	useBatchReadNotificationsMutation,
	useGetNotificationDetailQuery,
} from '@/api/notification/notificationApiSlice';
import {
	notificationRecordLabelKey,
	resolveNotificationRecordPath,
} from '@/common/components/notification/notificationHelper';
import { DISPLAY_DATE_TIME_FORMAT } from '@/utils/dateHelper';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Box, Button, Card, Divider, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

/** Keeps the body near 65–75 characters per line, the comfortable reading range. */
const MESSAGE_MAX_WIDTH = 720;

/**
 * Full text of one notification, plus the jump to the record it is about.
 *
 * Laid out as a message rather than a form: the notification is a letter, so it
 * reads subject → time → body → action, and the action sits at the end of the body
 * where the reader arrives, the way a call to action does in an email. The other
 * detail pages in the app put their buttons top-right, but those act on the record
 * being shown; this one navigates away to a different record entirely.
 *
 * Reaching this page is what marks the notification read; the summary table is
 * where a staff member can put it back to unread.
 */
export default function NotificationDetailPage() {
	const { t } = useTranslation();
	const { notificationId } = useParams();
	const navigate = useNavigate();

	// An unknown or malformed id comes back as 400 rather than 404, so any error
	// from this query is the "no longer available" state.
	const { data: detail, isError } = useGetNotificationDetailQuery(
		notificationId ?? '',
		{ skip: !notificationId, refetchOnMountOrArgChange: true }
	);
	const notification = detail?.data;

	const [batchReadNotifications] = useBatchReadNotificationsMutation();

	// opening the notification is what marks it read, whether the staff member got
	// here from the popover, the summary table or a bookmarked link. The GET does
	// not do it, and the call is idempotent, so an already-read one costs nothing.
	useEffect(() => {
		if (notification && !notification.isRead) {
			batchReadNotifications({ notificationIds: [notification.id] });
		}
	}, [notification?.id, notification?.isRead]);

	// The backend already puts the logged-in staff member first and flags them with
	// `isCurrent`, so the array is rendered in the order it arrives.
	const recipients = notification?.groupStaffs ?? [];
	const recordPath = notification
		? resolveNotificationRecordPath(notification)
		: undefined;
	const recordLabelKey = notification
		? notificationRecordLabelKey(notification)
		: undefined;

	return (
		<Box sx={{ width: '100%' }}>
			<Typography variant='h4'>{t('notification.details')}</Typography>

			<Card sx={{ marginTop: 3, maxWidth: 880, padding: '32px 40px' }}>
				{!notification ? (
					isError && (
						<Typography sx={{ fontSize: 15, color: 'common.neutral50' }}>
							{t('notification.notFound')}
						</Typography>
					)
				) : (
					<>
						<Typography
							component='h2'
							sx={{
								fontSize: 20,
								lineHeight: 1.4,
								fontWeight: 600,
								color: 'common.neutral20',
								maxWidth: MESSAGE_MAX_WIDTH,
							}}
						>
							{notification.title}
						</Typography>
						{/* the date needs no label: under a subject, in a message, it reads as "sent" */}
						<Typography sx={{ mt: 1, fontSize: 13, color: 'common.neutral50' }}>
							{dayjs(notification.createdAt)
								.locale('en')
								.format(DISPLAY_DATE_TIME_FORMAT)}
						</Typography>

						{/*
							Sits above the body, where an email puts its recipients, so the
							reader knows who else was told before hitting a "Dear <someone
							else>" greeting.

							Shown even when there is a single recipient: it positively answers
							"does anyone else know about this?", where an absent line would
							leave the reader unsure whether nobody else was told or the page
							just does not say.

							Roles are the short codes ("OIC/AOIC/CL", "CW") the Role column of
							System user management uses. The long forms would not fit — one of
							them is "Officer-In-Charge / Assistant Officer-In-Charge /
							Clinical Leader" on its own.
						*/}
						{recipients.length > 0 && (
							<Box
								sx={{
									display: 'flex',
									gap: 1.5,
									mt: 1.5,
									maxWidth: MESSAGE_MAX_WIDTH,
								}}
							>
								<Typography
									sx={{
										flexShrink: 0,
										fontSize: 13,
										color: 'common.neutral50',
									}}
								>
									{t('notification.sentTo')}
								</Typography>
								<Typography sx={{ fontSize: 13, color: 'common.neutral30' }}>
									{recipients.map((person, index) => (
										<Box
											key={person.staffId}
											component='span'
											sx={{ fontWeight: person.isCurrent ? 600 : 400 }}
										>
											{index > 0 && ', '}
											{person.isCurrent
												? t('notification.you')
												: person.staffName}
											{person.roleName ? ` (${person.roleName})` : ''}
										</Box>
									))}
								</Typography>
							</Box>
						)}

						<Divider sx={{ my: 3, borderColor: 'common.neutral90' }} />

						{/* `pre-line` keeps the paragraph breaks the notification template writes */}
						<Typography
							sx={{
								fontSize: 15,
								lineHeight: 1.75,
								color: 'common.neutral30',
								whiteSpace: 'pre-line',
								maxWidth: MESSAGE_MAX_WIDTH,
							}}
						>
							{notification.content}
						</Typography>

						{recordPath && recordLabelKey && (
							<>
								<Divider sx={{ my: 3, borderColor: 'common.neutral90' }} />
								<Button
									variant='contained'
									endIcon={<ArrowForwardIcon />}
									onClick={() => navigate(recordPath)}
								>
									{t(recordLabelKey)}
								</Button>
							</>
						)}
					</>
				)}
			</Card>
		</Box>
	);
}
